import { useState, useEffect, useRef, Fragment } from 'react';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import Toast from '../../components/common/Toast';

interface Role {
  id: string;
  name: string;
  code: string;
  isSystem: boolean;
}

interface Permission {
  id: string;
  name: string;
  code: string;
  resource: string;
  action: string;
}

interface MatrixRow {
  roleId: string;
  roleName: string;
  roleCode: string;
  isSystem: boolean;
  permissions: Array<{
    permissionId: string;
    permissionCode: string;
    permissionName: string;
    resource: string;
    action: string;
    enabled: boolean;
  }>;
}

interface MatrixData {
  roles: Role[];
  permissions: Permission[];
  matrix: MatrixRow[];
}

export default function PermissionMatrix() {
  const { hasPermission } = usePermission();
  const canManagePermissions = hasPermission('permissions.update');
  const [data, setData] = useState<MatrixData | null>(null);
  const initialPermSetRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Array<{
    roleId: string;
    permissionId: string;
    enabled: boolean;
  }>>([]);
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await api.get<MatrixData>('/api/permissions/matrix');
        if (cancelled) return;
        setData(result);
        const permSet = new Set<string>();
        result?.matrix?.forEach(row => {
          row.permissions?.forEach(p => { if (p.enabled) permSet.add(`${row.roleId}:${p.permissionId}`); });
        });
        initialPermSetRef.current = permSet;
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch permission matrix:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (pendingChanges.length > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingChanges.length]);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const result = await api.get<MatrixData>('/api/permissions/matrix');
      setData(result);
    } catch (error) {
      console.error('Failed to fetch permission matrix:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (roleId: string, permissionId: string, currentValue: boolean) => {
    const newValue = !currentValue;
    const initialValue = initialPermSetRef.current.has(`${roleId}:${permissionId}`);
    
    setPendingChanges(prev => {
      if (newValue === initialValue) {
        return prev.filter(c => !(c.roleId === roleId && c.permissionId === permissionId));
      }
      const existing = prev.findIndex(c => c.roleId === roleId && c.permissionId === permissionId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing].enabled = newValue;
        return updated;
      }
      return [...prev, { roleId, permissionId, enabled: newValue }];
    });

    // UIを更新
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        matrix: prev.matrix.map(row => {
          if (row.roleId !== roleId) return row;
          return {
            ...row,
            permissions: row.permissions.map(perm => {
              if (perm.permissionId !== permissionId) return perm;
              return { ...perm, enabled: newValue };
            }),
          };
        }),
      };
    });
  };

  const handleSave = async () => {
    if (pendingChanges.length === 0 || saving) return;
    
    setSaving(true);
    try {
      const response = await api.put<{ 
        message: string; 
        results: Array<{ roleId: string; permissionId: string; enabled: boolean; success: boolean }>;
        roles: Role[];
        permissions: Permission[];
        matrix: MatrixRow[];
      }>('/api/permissions/matrix', { updates: pendingChanges });
      
      // 結果を確認
      const failedUpdates = response.results?.filter(r => !r.success) || [];
      if (failedUpdates.length > 0) {
        setToast({message: '一部の権限の保存に失敗しました', type: 'error'});
      } else {
        setToast({message: '権限を保存しました', type: 'success'});
      }
      
      // レスポンスからマトリックスデータを使用
      setData({
        roles: response.roles,
        permissions: response.permissions,
        matrix: response.matrix,
      });
      
      setPendingChanges([]);
    } catch (error) {
      console.error('Failed to save permission matrix:', error);
      setToast({message: '権限の保存に失敗しました', type: 'error'});
      await fetchMatrix();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-gray-500">データの読み込みに失敗しました</div>;
  }

  // リソース別にグループ化
  const resourceGroups = (Array.isArray(data.permissions) ? data.permissions : []).reduce((acc, perm) => {
    if (!acc[perm.resource]) acc[perm.resource] = [];
    acc[perm.resource].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  const resourceLabels: Record<string, string> = {
    requests: '要請応対',
    constructions: '工事管理',
    companies: '企業管理',
    talkrooms: 'トークルーム',
    calendar: 'カレンダー',
    tasks: 'タスク管理',
    daily_reports: '日報管理',
    board: '掲示板',
    users: 'ユーザー管理',
    payslips: '給与明細',
    year_end: '年末調整',
    surveys: 'アンケート',
    masters: 'マスタ管理',
    permissions: '権限管理',
    groups: 'グループ管理',
    incentives: 'インセンティブ明細',
    billing_invoices: '請求書管理',
  };

  // リソースの表示順序
  const resourceOrder = [
    'requests', 'constructions', 'companies', 'talkrooms', 'calendar',
    'tasks', 'daily_reports', 'board', 'users', 'payslips', 'incentives',
    'year_end', 'surveys', 'groups', 'masters', 'permissions', 'billing_invoices',
  ];

  // アクションの表示順序（閲覧、作成、編集、削除）
  const actionOrder: Record<string, number> = {
    view: 0,
    create: 1,
    update: 2,
    delete: 3,
  };

  // 各リソースの権限をアクション順にソート
  const sortedResourceGroups = Object.fromEntries(
    Object.entries(resourceGroups).map(([resource, perms]) => [
      resource,
      [...perms].sort((a, b) => (actionOrder[a.action] ?? 99) - (actionOrder[b.action] ?? 99)),
    ])
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">権限マトリックス</h1>
          <p className="text-gray-500 mt-1 text-sm">ロールごとの権限を設定します</p>
        </div>
        {canManagePermissions && (
          <Button onClick={handleSave} loading={saving} disabled={pendingChanges.length === 0} className="w-full sm:w-auto">
            {pendingChanges.length > 0 ? `変更を保存 (${pendingChanges.length})` : '変更なし'}
          </Button>
        )}
      </div>

      <Card>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap sticky left-0 bg-gray-50 dark:bg-gray-700 z-10" style={{ minWidth: '150px' }}>
                    権限
                  </th>
                  {data.roles.map(role => (
                    <th key={role.id} className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                      <span className="hidden sm:inline">{role.name}</span>
                      <span className="sm:hidden" title={role.name}>{role.code ? role.code.slice(0, 8) : role.name.slice(0, 4)}</span>
                      {role.isSystem && <span className="ml-1 text-blue-500">*</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {resourceOrder
                  .filter(resource => sortedResourceGroups[resource])
                  .concat(Object.keys(sortedResourceGroups).filter(r => !resourceOrder.includes(r)))
                  .map(resource => [resource, sortedResourceGroups[resource]] as [string, Permission[]])
                  .filter(([, permissions]) => permissions && permissions.length > 0)
                  .map(([resource, permissions]) => (
                  <Fragment key={resource}>
                    <tr className="bg-gray-100 dark:bg-gray-600">
                      <td colSpan={data.roles.length + 1} className="px-3 sm:px-4 py-2 font-medium text-sm text-gray-700 dark:text-gray-200 sticky left-0 bg-gray-100 dark:bg-gray-600">
                        {resourceLabels[resource] || resource}
                      </td>
                    </tr>
                    {permissions.map(permission => (
                      <tr key={permission.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 sm:px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800">
                          {permission.name}
                        </td>
                        {data.matrix.map(row => {
                          const perm = row.permissions.find(p => p.permissionId === permission.id);
                          return (
                            <td key={`${row.roleId}-${permission.id}`} className="px-2 sm:px-4 py-2 text-center">
                              <button
                                onClick={() => handleToggle(row.roleId, permission.id, perm?.enabled || false)}
                                className={`w-6 h-6 rounded transition-colors ${
                                  perm?.enabled
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-200 dark:bg-gray-600'
                                } hover:opacity-80`}
                              >
                                {perm?.enabled && (
                                  <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          <span className="text-blue-500">*</span> システムロールは削除できません。権限のON/OFFを変更後、「変更を保存」ボタンをクリックしてください。
        </div>
      </Card>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
